import React, { useState, useEffect, useRef } from "react";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";

const services = [
    {
        id: 1,
        title: "Proven Engineering Expertise",
        category: "service",
        desc: "Our developers bring hands-on experience across enterprise, startup, and global delivery environments, ensuring high-quality outcomes from day one.",
        defaultBg: "bg-gradient-to-b from-[#01386A] to-[#1C355E]", // Deep Blue Gradient
    },
    {
        id: 2,
        title: "Flexible Engagement Models",
        category: "service",
        desc: "Our developers bring hands-on experience across enterprise, startup, and global delivery environments, ensuring high-quality outcomes from day one.",
        defaultBg: "bg-gradient-to-r from-[#01386A] to-[#4B6CB7]", // Blue to Royal Blue (Replaced Orange)
    },
    {
        id: 3,
        title: "Faster Time to Market",
        category: "service",
        desc: "Innovative product design, development, and scaling for global markets.",
        defaultBg: "bg-gradient-to-br from-[#01386A] to-[#8E9EAB]", // Blue to Grayish Blue
    },
    {
        id: 4,
        title: "Secure & Compliant",
        category: "service",
        desc: "Optimize systems for peak performance, resilience, and efficiency.",
        defaultBg: "bg-gradient-to-bl from-[#01386A] to-[#6A85B6]", // Blue to Soft Blue
    },
    {
        id: 5,
        title: "ServiceNow Consulting",
        category: "service",
        desc: "Transform workflows with expert ServiceNow implementation and consulting.",
        defaultBg: "bg-gradient-to-tr from-[#01386A] to-[#253B5C]", // Blue to Slate Blue (Replaced Black)
    },
    {
        id: 6,
        title: "Infor M3 Solutions",
        category: "service",
        desc: "Empowering enterprises with tailored Infor M3 ERP solutions and support.",
        defaultBg: "bg-gradient-to-t from-[#01386A] to-[#243B55]", // Blue to Dark Navy
    },
    {
        id: 7,
        title: "MES Solutions",
        category: "service",
        desc: "Assessments, maturity analysis, roadmap design, and business case development for MES adoption or enhancement.",
        defaultBg: "bg-gradient-to-tl from-[#01386A] to-[#141E30]", // Blue to Midnight Blue (Replaced Black)
    },
    // {
    //     id: 8,
    //     title: "Building Automation Solutions",
    //     category: "service",
    //     desc: "We provide end-to-end Building Automation and Building Management System (BMS) solutions, covering complete control system and instrumentation design, from field devices and equipment to centralized monitoring, control, and data acquisition systems.",
    //     defaultBg: "bg-gradient-to-l from-[#01386A] to-[#0E2A62]",
    // },
    {
        id: 9,
        title: "Industrial Automation Solutions",
        category: "service",
        desc: "We provide end-to-end industrial automation solutions, delivering complete instrumentation and control system design from field devices to PLC, DCS, SCADA, and data acquisition systems.",
        defaultBg: "bg-gradient-to-br from-[#004e92] to-[#01386A]", // Vibrant Blue to Blue (Replaced Black)
    },
];

const WhyMobikode = () => {
    const [loaded, setLoaded] = useState(false);

    // --- KEEN SLIDER CONFIG WITH AUTOPLAY ---
    const [sliderRef, instanceRef] = useKeenSlider(
        {
            initial: 0,
            created() {
                setLoaded(true);
            },
            loop: true,
            mode: "snap",
            slides: {
                perView: 1.2,
                spacing: 20,
            },
            breakpoints: {
                "(min-width: 640px)": { slides: { perView: 2.2, spacing: 20 } },
                "(min-width: 1024px)": { slides: { perView: 3.2, spacing: 30 } },
                "(min-width: 1280px)": { slides: { perView: 3.5, spacing: 40 } },
            },
        },
        [
            // --- AUTOPLAY PLUGIN ---
            (slider) => {
                let timeout;
                let mouseOver = false;

                function clearNextTimeout() {
                    clearTimeout(timeout);
                }

                function nextTimeout() {
                    clearTimeout(timeout);
                    if (mouseOver) return;
                    timeout = setTimeout(() => {
                        slider.next();
                    }, 2000); // 2 Seconds Speed (Change here if needed)
                }

                slider.on("created", () => {
                    slider.container.addEventListener("mouseover", () => {
                        mouseOver = true;
                        clearNextTimeout();
                    });
                    slider.container.addEventListener("mouseout", () => {
                        mouseOver = false;
                        nextTimeout();
                    });
                    nextTimeout();
                });
                slider.on("dragStarted", clearNextTimeout);
                slider.on("animationEnded", nextTimeout);
                slider.on("updated", nextTimeout);
            },
        ]
    );

    return (
        <section className="py-[40px] lg:py-[80px] bg-white font-sans relative overflow-hidden flex flex-col justify-center">

            <div className="absolute inset-0 z-0 opacity-40 pointer-events-none">
                <FlowingMeshCanvas />
            </div>

            <div className="max-w-[1440px] w-full mx-auto px-[20px] lg:px-[80px] relative z-10">
                <div className="flex justify-between items-center lg:mb-[60px] mb-[30px]">
                    <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] relative z-10">
                        Why Hire Developers from Mobikode?
                    </h2>
                    {loaded && instanceRef.current && (
                        <div className="flex items-center gap-4 mt-[36px]">
                            <NavButton
                                onClick={(e) => e.stopPropagation() || instanceRef.current?.prev()}
                                icon={<ArrowLeft size={20} />}
                            />
                            <NavButton
                                onClick={(e) => e.stopPropagation() || instanceRef.current?.next()}
                                icon={<ArrowRight size={20} />}
                            />
                        </div>
                    )}

                </div>

                <div ref={sliderRef} className="keen-slider py-10 -my-10">
                    {services.map((service) => (
                        <div key={service.id} className="keen-slider__slide">
                            <ServiceCard service={service} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};


const ServiceCard = ({ service }) => {
    return (
        <motion.div
            initial="initial"
            whileHover="hover"
            animate="initial"
            className={`
                relative h-[420px] overflow-hidden cursor-pointer group shadow-lg
                ${service.defaultBg || 'bg-[#000]'} transition-colors duration-500
            `}
        >

            <div className="relative z-20 h-full flex flex-col p-[30px]">
                <div className="relative z-20">
                    {/* <h4 className="text-white font-sora font-[600] text-[14px] tracking-widest uppercase opacity-80 mb-3">
                        {service.category || "Service"} 
                    </h4> */}
                    <h3 className="text-white font-sora font-[600] lg:text-[20px] text-[16px] leading-[1.1]">
                        {service.title}
                    </h3>
                </div>

                {/* --- B. DESCRIPTION (Slides in from Right on Hover) --- */}
                <div className="flex-1 flex items-center relative overflow-hidden">
                    <motion.p
                        className="text-white/90 font-graphik lg:text-[16px] text-[14px] font-[500] leading-[26px]"
                        variants={{
                            initial: { x: 50, opacity: 0 }, // Right side mein chhupa hua
                            hover: { x: 0, opacity: 1 }     // Slide karke wapas aayega
                        }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                    >
                        {service.desc}
                    </motion.p>
                </div>

                {/* --- C. BOTTOM LINK ("Read More") --- */}
                <motion.div
                    className="flex items-center justify-end mt-auto"
                    variants={{
                        initial: { y: 20, opacity: 0 },
                        hover: { y: 0, opacity: 1 }
                    }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                >
                    <div className="group flex items-center gap-2 text-white font-graphik font-[500] text-[16px]">
                        <span className="opacity-90 group-hover:opacity-100 transition-opacity">Read More</span>
                        <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                </motion.div>

            </div>

            {/* Border Overlay */}
            <div className="absolute inset-0 border border-white/10 pointer-events-none z-30" />
        </motion.div>
    );
};

// --- 🎨 CANVAS COMPONENT (UNCHANGED) ---
const FlowingMeshCanvas = () => {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        let animationFrameId;
        let time = 0;
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        window.addEventListener("resize", resizeCanvas);
        resizeCanvas();
        const draw = () => {
            const { width, height } = canvas;
            ctx.clearRect(0, 0, width, height);
            const lines = 50;
            const amplitude = 80;
            const frequency = 0.002;
            const speed = 0.003;
            ctx.lineWidth = 0.8;
            for (let i = 0; i < lines; i++) {
                ctx.beginPath();
                const alpha = 0.1 + (i / lines) * 0.3;
                ctx.strokeStyle = `hsla(217, 54%, 24%, ${alpha})`;
                for (let x = 0; x <= width; x += 5) {
                    const yOffset = height / 2;
                    const y1 = Math.sin(x * frequency + time + i * 0.05) * amplitude;
                    const y2 = Math.sin(x * frequency * 0.5 + time * 0.5) * (amplitude * 0.5);
                    const tilt = (x / width) * 50;
                    ctx.lineTo(x, yOffset + y1 + y2 + tilt + (i * 3));
                }
                ctx.stroke();
            }
            time += speed;
            animationFrameId = requestAnimationFrame(draw);
        };
        draw();
        return () => {
            window.removeEventListener("resize", resizeCanvas);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);
    return <canvas ref={canvasRef} className="w-full h-full" />;
};

// --- NAV BUTTON (UNCHANGED) ---
const NavButton = ({ onClick, icon }) => (
    <button
        onClick={onClick}
        className="w-[32px] h-[32px] rounded-full border border-[#ED6800] text-[#ED6800] flex items-center justify-center hover:bg-[#ED6800] hover:text-white transition-all duration-300 relative z-20"
    >
        {icon}
    </button>
);

export default WhyMobikode;